import { useEffect, useMemo, useRef, useState } from "react";
import { type ColumnDef, type ColumnFiltersState } from "@tanstack/react-table";
import { PageLayout } from "@/ui/layouts";
import { Button, Card, DialogFooter, Modal } from "@/ui/atoms";
import {
    DataTable,
    EmptyState,
    MasterCodeUsageNotice,
    type TMasterCodeUsage,
} from "@/ui/molecules";
import { useMasterCodeUsage } from "@/useCases/useMasterCodeUsage";
import { useServerTable, type TServerTableQueryInput } from "@/useCases/useServerTable";
import { buildRowCount } from "@/useCases/rowCount";
import type { TApiPropcodeMasterUploadResponse } from "@/apis/proda_finance/types";
import { MapPin, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { PropertyCodeFormModal, type TPropertyCodeDraft } from "./PropertyCodeFormModal";

export type TPropertyCodeRow = {
    id: string;
    code: string;
    description: string | null;
    isActive: boolean;
};

export type TPropertyCodesQuery = {
    page: number;
    page_size: number;
    code?: string;
    description?: string;
    is_active?: boolean;
    sort_by?: string;
    sort_dir?: "asc" | "desc";
};

const PAGE_SIZE = 50;

const firstString = (v: unknown): string | undefined =>
    typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;
const asBool = (v: unknown): boolean | undefined => (v === true || v === false ? v : undefined);

const toQuery = (input: TServerTableQueryInput): TPropertyCodesQuery => {
    const get = (id: string) => input.columnFilters.find((f) => f.id === id)?.value;
    const sort = input.sorting[0];
    return {
        page: input.page,
        page_size: input.pageSize,
        code: firstString(get("code")),
        description: firstString(get("description")),
        is_active: asBool(get("isActive")),
        sort_by: sort?.id,
        sort_dir: sort ? (sort.desc ? "desc" : "asc") : undefined,
    };
};

export type TViewPropertyCodesParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        onQueryChange: (query: TPropertyCodesQuery, signal?: AbortSignal) => void;
        usePropertyCodesPage: () => {
            items: TPropertyCodeRow[];
            total: number;
            totalUnfiltered: number;
            isLoading: boolean;
            hasError: boolean;
        };
        onCreatePropertyCode: (data: {
            property_code: string;
            property_name: string;
            is_active: boolean;
        }) => Promise<void>;
        onUpdatePropertyCode: (
            id: string,
            data: { property_code: string; property_name: string; is_active: boolean },
        ) => Promise<void>;
        onDeletePropertyCode: (id: string) => Promise<void>;
        onLoadPropertyCodeUsage: (id: string) => Promise<TMasterCodeUsage>;
        onUploadFile: (file: File) => Promise<TApiPropcodeMasterUploadResponse>;
        permissions: () => string[];
    };
};

export type TViewPropertyCodesProps = Record<string, never>;

export const createViewPropertyCodes = ({ useCases }: TViewPropertyCodesParams) => {
    const ViewPropertyCodes: React.FC<TViewPropertyCodesProps> = () => {
        const {
            onMount,
            onUnmount,
            onQueryChange,
            usePropertyCodesPage,
            onCreatePropertyCode,
            onUpdatePropertyCode,
            onDeletePropertyCode,
            onLoadPropertyCodeUsage,
            onUploadFile,
            permissions,
        } = useCases();

        const perms = permissions();
        const canUpload = perms.includes("master_propcode_upload");
        const canCreate = perms.includes("settings_propcode_line_create");
        const canEdit = perms.includes("settings_propcode_line_edit");
        const canDelete = perms.includes("settings_propcode_line_delete");

        const page = usePropertyCodesPage();
        const [isUploading, setIsUploading] = useState(false);
        const [uploadResult, setUploadResult] = useState<TApiPropcodeMasterUploadResponse | null>(
            null,
        );
        const fileInputRef = useRef<HTMLInputElement>(null);

        // Modal-driven create/edit/delete
        const [createOpen, setCreateOpen] = useState(false);
        const [editRow, setEditRow] = useState<TPropertyCodeRow | null>(null);
        const [deleteRow, setDeleteRow] = useState<TPropertyCodeRow | null>(null);
        const [isSaving, setIsSaving] = useState(false);
        const [formError, setFormError] = useState<string | null>(null);
        const deleteUsage = useMasterCodeUsage(deleteRow?.id ?? null, onLoadPropertyCodeUsage);

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        const {
            onColumnFiltersChange,
            onSortingChange,
            sorting,
            setPage,
            page: currentPage,
            refetch,
            columnFilters,
        } = useServerTable<TPropertyCodesQuery>({
            pageSize: PAGE_SIZE,
            toQuery,
            fetchData: onQueryChange,
        });

        // Edit/delete: re-run the current query in place (the affected row is on
        // the page being viewed).
        const refresh = (): void => refetch();
        // Create/upload: a new row (or a replaced dataset) may not belong on the
        // current page, so reset to page 1 and force the fetch.
        const refreshToStart = (): void => {
            setPage(1);
            refetch();
        };

        const submitCreate = async (draft: TPropertyCodeDraft): Promise<void> => {
            setFormError(null);
            setIsSaving(true);
            try {
                await onCreatePropertyCode(draft);
                setCreateOpen(false);
                refreshToStart();
            } catch (err) {
                setFormError(err instanceof Error ? err.message : "Failed to create property code");
            } finally {
                setIsSaving(false);
            }
        };

        const submitEdit = async (draft: TPropertyCodeDraft): Promise<void> => {
            if (!editRow) return;
            setFormError(null);
            setIsSaving(true);
            try {
                await onUpdatePropertyCode(editRow.id, draft);
                setEditRow(null);
                refresh();
            } catch (err) {
                setFormError(err instanceof Error ? err.message : "Failed to update property code");
            } finally {
                setIsSaving(false);
            }
        };

        const submitDelete = async (): Promise<void> => {
            if (!deleteRow) return;
            setIsSaving(true);
            setFormError(null);
            try {
                await onDeletePropertyCode(deleteRow.id);
                setDeleteRow(null);
                refresh();
            } catch (err) {
                setFormError(err instanceof Error ? err.message : "Failed to delete property code");
            } finally {
                setIsSaving(false);
            }
        };

        const columns: ColumnDef<TPropertyCodeRow>[] = useMemo(
            () => [
                {
                    accessorKey: "code",
                    id: "code",
                    header: "Code",
                    meta: { filterType: "text", filterPlaceholder: "Search code…" },
                    cell: ({ row }) => (
                        <span className="font-mono font-medium">{row.original.code}</span>
                    ),
                },
                {
                    accessorKey: "description",
                    id: "description",
                    header: "Description",
                    meta: { filterType: "text", filterPlaceholder: "Search description…" },
                    cell: ({ row }) => <span>{row.original.description ?? "—"}</span>,
                },
                {
                    accessorKey: "isActive",
                    id: "isActive",
                    header: "Status",
                    meta: { filterType: "boolean" },
                    cell: ({ row }) =>
                        row.original.isActive ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                Active
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                Inactive
                            </span>
                        ),
                },
                {
                    id: "actions",
                    header: "Actions",
                    enableSorting: false,
                    meta: { hidePerColumnTextSearch: true },
                    cell: ({ row }) => (
                        <div className="inline-flex justify-end gap-2 w-full">
                            {canEdit && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        setFormError(null);
                                        setEditRow(row.original);
                                    }}
                                >
                                    <Pencil className="h-3 w-3 mr-1" />
                                    Edit
                                </Button>
                            )}
                            {canDelete && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        setFormError(null);
                                        setDeleteRow(row.original);
                                    }}
                                >
                                    <Trash2 className="h-3 w-3 mr-1" />
                                    Delete
                                </Button>
                            )}
                        </div>
                    ),
                },
            ],
            [canEdit, canDelete],
        );

        const handleFileSelected = async (
            event: React.ChangeEvent<HTMLInputElement>,
        ): Promise<void> => {
            const file = event.target.files?.[0];
            if (!file) return;
            setIsUploading(true);
            setUploadResult(null);
            try {
                const result = await onUploadFile(file);
                setUploadResult(result);
                if (result.success) refreshToStart();
            } catch {
                setUploadResult({
                    success: false,
                    imported_count: 0,
                    new_count: 0,
                    updated_count: 0,
                    deactivated_count: 0,
                    skipped_deactivation: [],
                    skipped_count: 0,
                    errors: ["Upload failed. Please try again."],
                    warnings: [],
                    version_number: null,
                });
            } finally {
                setIsUploading(false);
                if (fileInputRef.current) fileInputRef.current.value = "";
            }
        };

        if (page.hasError) {
            return (
                <PageLayout
                    title="Property Codes"
                    description="Manage property identifiers"
                    icon={<MapPin className="h-6 w-6" />}
                >
                    <EmptyState
                        variant="error"
                        title="Failed to load property codes"
                        description="An error occurred while loading property data"
                        action={{ label: "Try Again", onClick: () => refetch() }}
                    />
                </PageLayout>
            );
        }

        return (
            <PageLayout
                title="Property Codes"
                description="Manage property identifiers"
                icon={<MapPin className="h-6 w-6" />}
                actions={
                    <div className="flex gap-2">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx"
                            className="hidden"
                            data-testid="propcode-upload-input"
                            onChange={(event) => void handleFileSelected(event)}
                        />
                        {canUpload && (
                            <Button
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isUploading}
                            >
                                <Upload className="mr-2 h-4 w-4" />
                                {isUploading ? "Uploading..." : "Upload Excel"}
                            </Button>
                        )}
                        {canCreate && (
                            <Button
                                onClick={() => {
                                    setFormError(null);
                                    setCreateOpen(true);
                                }}
                            >
                                <Plus className="mr-2 h-4 w-4" />
                                Add New
                            </Button>
                        )}
                    </div>
                }
            >
                {uploadResult && (
                    <div
                        className={`mb-4 rounded-lg border p-4 ${
                            uploadResult.success
                                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                : "border-red-200 bg-red-50 text-red-800"
                        }`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                {uploadResult.success ? (
                                    <>
                                        <p className="font-medium">
                                            Upload successful (v{uploadResult.version_number})
                                        </p>
                                        <p className="mt-1 text-sm">
                                            New: {uploadResult.new_count} · Updated:{" "}
                                            {uploadResult.updated_count} · Deactivated:{" "}
                                            {uploadResult.deactivated_count}
                                        </p>
                                        {uploadResult.skipped_deactivation.length > 0 && (
                                            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-800">
                                                <p className="text-sm font-medium">
                                                    The following codes could not be deactivated —
                                                    they still have active mappings:
                                                </p>
                                                <ul className="mt-1 list-disc pl-5 text-sm">
                                                    {uploadResult.skipped_deactivation.map(
                                                        (code) => (
                                                            <li key={code}>{code}</li>
                                                        ),
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                        {uploadResult.warnings.length > 0 && (
                                            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-800">
                                                <p className="text-sm font-medium">
                                                    Imported with fallback values — check these rows
                                                    in the source file:
                                                </p>
                                                <ul className="mt-1 list-disc pl-5 text-sm">
                                                    {uploadResult.warnings.map((warning, i) => (
                                                        <li key={i}>{warning}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    uploadResult.errors.map((err, i) => (
                                        <p key={i} className="text-sm">
                                            {err}
                                        </p>
                                    ))
                                )}
                            </div>
                            <button
                                onClick={() => setUploadResult(null)}
                                className="text-current opacity-50 hover:opacity-100 ml-4"
                                aria-label="Dismiss"
                            >
                                &times;
                            </button>
                        </div>
                    </div>
                )}

                <Card className="overflow-hidden">
                    <DataTable
                        columns={columns}
                        data={page.items}
                        loading={page.isLoading}
                        manualFiltering
                        manualSorting
                        sortingState={sorting}
                        onSortingChange={onSortingChange}
                        onColumnFiltersChange={
                            onColumnFiltersChange as (f: ColumnFiltersState) => void
                        }
                        rowNoun={{ one: "property code", many: "property codes" }}
                        rowCount={buildRowCount({
                            total: page.total,
                            totalUnfiltered: page.totalUnfiltered,
                            hasActiveFilters: columnFilters.length > 0,
                        })}
                        pagination={{
                            page: currentPage,
                            pageSize: PAGE_SIZE,
                            total: page.total,
                            onPageChange: setPage,
                        }}
                        storageKey="property-codes"
                        emptyMessage="No property codes match the current filters."
                    />
                </Card>

                <PropertyCodeFormModal
                    open={createOpen}
                    mode="create"
                    isSaving={isSaving}
                    error={formError}
                    onSubmit={(draft) => void submitCreate(draft)}
                    onClose={() => setCreateOpen(false)}
                />

                <PropertyCodeFormModal
                    open={editRow !== null}
                    mode="edit"
                    initial={
                        editRow
                            ? {
                                  property_code: editRow.code,
                                  property_name: editRow.description ?? "",
                                  is_active: editRow.isActive,
                              }
                            : undefined
                    }
                    isSaving={isSaving}
                    error={formError}
                    onSubmit={(draft) => void submitEdit(draft)}
                    onClose={() => setEditRow(null)}
                />

                <Modal
                    open={deleteRow !== null}
                    onOpenChange={(next) => {
                        if (!next) {
                            setDeleteRow(null);
                            setFormError(null);
                        }
                    }}
                    title="Delete Property Code"
                    description={
                        deleteRow
                            ? `Delete ${deleteRow.code}? You can turn it back on later by editing it.`
                            : undefined
                    }
                >
                    <MasterCodeUsageNotice
                        {...deleteUsage}
                        ownerNoun={{ one: "deal", many: "deals" }}
                        consequence="Deleting the code does not remove these mappings: lines they resolve to it will fail validation as an inactive property code until the mappings are changed."
                    />
                    {formError && (
                        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                            {formError}
                        </div>
                    )}
                    <DialogFooter className="gap-2 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setDeleteRow(null);
                                setFormError(null);
                            }}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={() => void submitDelete()}
                            disabled={isSaving}
                        >
                            {isSaving ? "Deleting..." : "Delete"}
                        </Button>
                    </DialogFooter>
                </Modal>
            </PageLayout>
        );
    };

    return ViewPropertyCodes;
};
